import { type HttpContext } from '@angular/common/http';
import {
  Component,
  computed,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import { BaseStitchListComponent, StitchListItem } from '@modules/common/components/list/base-stitch-list.component';
import { ScrollToPosition } from '@modules/common/services/scroll-to-index.injection-token';
import { Like } from '@modules/common/types/like';
import { type StitchCardSelection, type StitchCardVariant } from '@modules/common/utils/stitch-card-variant';
import { type SelectableOpenEvent } from '@modules/drag-n-drop/types/selectable-open-event';
import { FolderComponent } from '@modules/elements/components/folder/folder.component';
import { FilesTenantScopeService } from '@modules/files/services/files-tenant-scope.service';
import { FoldersService } from '@modules/files/services/folders.service';
import { FilesAppStateService } from '@modules/files/services/state.service';
import { File } from '@modules/files/types/file';
import { Folder } from '@modules/files/types/folder';
import { FoldersFilters } from '@modules/files/types/folders-filters';
import { LinkedInfoService } from '@modules/linked-info/services/linked-info.service';
import type { KnotSource } from '@sharedlib/common/types/knowledge/knot-source';
import { StitchType } from '@sharedlib/common/types/StitchType';
import { BehaviorSubject, merge, Observable } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';

@Component({
  selector: 'app-folders-list',
  templateUrl: './folders-list.component.html',
  styleUrls: ['./folders-list.component.less'],
  providers: [{ provide: ScrollToPosition, useFactory: () => new BehaviorSubject<number>(null) }],
  standalone: false,
})
export class FoldersListComponent extends BaseStitchListComponent<Folder> implements OnInit, OnDestroy, OnChanges {
  public readonly requestContext = input<HttpContext>();
  public readonly tenantItems = input<ReadonlyArray<{ readonly accountId?: string }>>();

  /** Show each row's hover quick actions; off for lists that only preview their items. */
  public readonly actionsButtonEnabled = input<boolean>(true);

  /** Container treatment forwarded to every card; the knowledge panel asks for `'card'`. */
  public readonly variant = input<StitchCardVariant>('row');

  /** Id of the item open in the dynamic panel, forwarded so that card draws itself as selected. */
  public readonly previewedItemId = input<string | null>(null);

  /** What each card draws as selected, forwarded; the knowledge panel asks for the list's own selection. */
  public readonly cardSelection = input<StitchCardSelection>('preview');

  /** The knowledge panel draws its results as cards; every other caller keeps the app's flush row. */
  protected readonly isCard = computed(() => this.variant() === 'card');

  @Input()
  public override filters: Like<FoldersFilters> = {};
  @Input()
  public withTags = false;
  @Input()
  public withKnots = false;
  @Input()
  public knotsSource: KnotSource;
  @Input()
  public debug: 'score';

  @Output()
  public readonly clickFolder = new EventEmitter<Folder>();
  @Output()
  public readonly openFolder = new EventEmitter<Folder>();
  @Output()
  public readonly openFile = new EventEmitter<File>();
  @Output()
  public readonly doubleClickFolder = new EventEmitter<Folder>();

  @ViewChildren(FolderComponent, { read: ElementRef<HTMLElement> })
  public override itemElements: QueryList<ElementRef<HTMLElement>>;

  public override items: StitchListItem<Folder>[] = [];

  private readonly foldersService = inject(FoldersService);
  private readonly filesStateService = inject(FilesAppStateService);
  private readonly linkedInfoService = inject(LinkedInfoService);
  private readonly filesTenantScopeService = inject(FilesTenantScopeService, { optional: true });
  /**
   * The owners of the rows the CURRENT query has answered with (#1598 reader F2). The Snoozed GET carries one
   * tenant (the Files tenant) while its rows can belong to a member or another workspace, and a row's menu signals
   * under the row's owner, so the list listens there too. Kept per query, not read off the listed rows: once an
   * owner's last row left, snoozing it again must still be heard. Filled from each page response, emptied when the
   * query changes (`resetItems`), and not used once the context or the Files tenant has moved.
   */
  private queryOwners: { readonly scope: string; readonly owners: Set<string> } = { scope: '', owners: new Set() };

  protected override stitchSignalSpaceIds(): ReadonlyArray<string> {
    const spaceIds = new Set<string>([this.spaceContextStore.contextId()]);
    const effectiveTenantId = this.filesTenantScopeService?.effectiveTenantId();
    if (effectiveTenantId) {
      spaceIds.add(effectiveTenantId);
    }
    for (const { accountId } of this.tenantItems() ?? []) {
      if (accountId) {
        spaceIds.add(accountId);
      }
    }
    if (this.queryOwners.scope === this.queryScope()) {
      for (const owner of this.queryOwners.owners) {
        spaceIds.add(owner);
      }
    }
    return [...spaceIds];
  }

  /** What the owners were collected under: the space context and the Files tenant the query was sent in. */
  private queryScope(): string {
    return `${this.spaceContextStore.contextId()}|${this.filesTenantScopeService?.effectiveTenantId() ?? ''}`;
  }

  protected override getStitchType(): StitchType {
    return StitchType.FOLDER;
  }

  public override ngOnInit() {
    merge(this.filesStateService.getRefreshAll(), this.linkedInfoService.getRefresh(), this.foldersService.getRefresh())
      .pipe(takeUntil(this.alive))
      .subscribe(() => {
        this.loadPage(this.page);
      });

    super.ngOnInit();
  }

  public ngOnChanges(changes: SimpleChanges) {
    if ('filters' in changes) {
      this.resetItems();
    }
  }

  /**
   * Collects the owners of each page the current query answers with (#1598 reader F2), around the base load so
   * `getItems` stays a plain request builder.
   */
  protected override handleDataLoad(
    offset: number,
    limit: number,
    scrollToTop?: boolean,
  ): Observable<{ items: Folder[]; count: number }> {
    const scope = this.queryScope();
    if (this.queryOwners.scope !== scope) {
      this.queryOwners = { scope, owners: new Set() };
    }
    const { owners } = this.queryOwners;
    return super.handleDataLoad(offset, limit, scrollToTop).pipe(
      tap(({ items }) => {
        for (const { accountId } of items ?? []) {
          if (accountId) {
            owners.add(accountId);
          }
        }
      }),
    );
  }

  protected override getItems(offset: number, limit: number): Observable<{ items: Folder[]; count: number }> {
    return this.foldersService.search(
      { ...this.filters, limit, offset },
      {
        withTags: this.withTags,
        withKnots: this.withKnots,
        withConnections: false,
        knotsSource: this.knotsSource,
        tenantItems: this.tenantItems(),
        requestContext: this.requestContext(),
      },
    );
  }

  public resetItems() {
    this.queryOwners = { scope: this.queryScope(), owners: new Set() };
    this.resetSelected.next();
    super.resetItems();
  }

  public handleDoubleClickItem(folder: Folder) {
    this.doubleClickFolder.emit(folder);
  }

  public handleOpenSelected(event: SelectableOpenEvent<Folder>): void {
    this.clickFolder.emit(event.item.data);
  }
}
